import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { wpost, wput } from '@/components/website/api';

export const STATUS_META = {
  live: { label: 'Live', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  offline: { label: 'Offline', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  transferred: { label: 'Transferred', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  unknown: { label: 'Unknown', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  in_progress: { label: 'In progress', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

const EMPTY = { customer: '', domain: '', built_on: '', built_by: '', hosting: '', renewal_date: '', status: 'live', contact_name: '', contact_phone: '', contact_email: '', notes: '' };

export function WebsiteEntryDialog({ open, onClose, entry, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ ...EMPTY, ...(entry || {}) }); }, [open, entry]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? '']));
      const d = entry?.id ? await wput(`/staff/website-log/${entry.id}`, body) : await wpost('/staff/website-log', body);
      toast.success(entry?.id ? 'Website updated' : 'Website added to the log'); onSaved(d); onClose();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const F = ({ label, k, type = 'text', placeholder }) => (
    <label className="block text-xs font-semibold text-gray-700">{label}<Input type={type} value={f[k]} onChange={set(k)} placeholder={placeholder} className="mt-1" data-testid={`wl-field-${k}`} /></label>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="website-entry-dialog">
        <DialogHeader><DialogTitle>{entry?.id ? 'Edit website' : 'Add a website'}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-3">
          <F label="Customer / company" k="customer" />
          <F label="Domain" k="domain" placeholder="example.co.uk" />
          <F label="Date built" k="built_on" placeholder="e.g. 2021 or 2021-05-01" />
          <F label="Built by" k="built_by" />
          <F label="Hosting / platform" k="hosting" placeholder="Fasthosts, Wix, Ox hosting…" />
          <F label="Hosting renewal date" k="renewal_date" type="date" />
          <label className="block text-xs font-semibold text-gray-700">Status
            <select value={f.status} onChange={set('status')} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid="wl-field-status">
              {['live', 'offline', 'transferred', 'unknown'].map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          </label>
          <F label="Contact name" k="contact_name" />
          <F label="Contact phone" k="contact_phone" />
          <div className="sm:col-span-2"><F label="Contact email" k="contact_email" type="email" /></div>
          <label className="sm:col-span-2 block text-xs font-semibold text-gray-700">Notes<Textarea value={f.notes} onChange={set('notes')} rows={3} className="mt-1" data-testid="wl-field-notes" /></label>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="wl-save">{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
